import { Tag } from 'antd';
import { TextBodyRegular, TextFootnoteDescription } from 'lib.typography';
import type { PartnerMatchItem } from 'lib.store/src/type-attendee/match/models';
import { formatDate } from 'lib.shared/src/utils';
import './styles.scss';

/**
 * The scorer is told to send plain text, but a slipped-in markdown emphasis
 * is stripped at render rather than rejected — shared by every surface that
 * prints the reasoning summary.
 */
export const stripEmphasis = (text: string): string => text.replace(/\*\*/g, '');

/** "9/29/2026 — 10/1/2026" for the match's event, or null without dates */
export const eventDates = (match: PartnerMatchItem): string | null => {
  const started = match.event?.date.started_at;
  const finished = match.event?.date.finished_at;
  if (!started) return null;
  return `${formatDate(new Date(started))}${finished ? ` — ${formatDate(new Date(finished))}` : ''}`;
};

export const hasReasoningDetails = (match: PartnerMatchItem): boolean =>
  !!(
    match.reasoning?.criteria?.length ||
    match.reasoning?.shared_ground?.length ||
    match.reasoning?.icebreakers?.length
  );

/**
 * The full "why this match" argument: criteria with evidence, common ground,
 * icebreakers.
 *
 * The argument is long by nature, so the structure carries it instead of the
 * prose: every part is labelled the same way, criteria are separated rows,
 * common ground reads as chips and each icebreaker gets its own line — a wall
 * of paragraphs is what an attendee stops reading.
 */
export const MatchReasoningDetails = ({ match }: { match: PartnerMatchItem }) => (
  <div className="match-reasoning">
    {match.reasoning?.criteria?.map((criterion) => (
      <div key={criterion.name} className="match-reasoning__row">
        {/* the numeric scores are internal calibration — the card argues in prose */}
        <TextFootnoteDescription className="match-reasoning__label">
          {criterion.name}
        </TextFootnoteDescription>
        {criterion.evidence && (
          <TextBodyRegular className="match-reasoning__evidence">
            {criterion.evidence}
          </TextBodyRegular>
        )}
      </div>
    ))}

    {!!match.reasoning?.shared_ground?.length && (
      <div className="match-reasoning__row">
        <TextFootnoteDescription className="match-reasoning__label">
          Common ground
        </TextFootnoteDescription>
        <div className="match-reasoning__chips">
          {match.reasoning.shared_ground.map((ground) => (
            <Tag key={ground} className="match-reasoning__chip" bordered={false}>
              {ground}
            </Tag>
          ))}
        </div>
      </div>
    )}

    {!!match.reasoning?.icebreakers?.length && (
      <div className="match-reasoning__row">
        <TextFootnoteDescription className="match-reasoning__label">
          Icebreakers
        </TextFootnoteDescription>
        <ul className="match-reasoning__icebreakers">
          {match.reasoning.icebreakers.map((icebreaker) => (
            <li key={icebreaker}>
              <TextBodyRegular className="match-reasoning__evidence">{icebreaker}</TextBodyRegular>
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);
